import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { OrderItemQuotesApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";

const apiClient = createApiClient(OrderItemQuotesApi);

export const useOrderItemQuotes = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const queryClient = useQueryClient();

  const useAddOrderItemsQuote = () =>
    useMutation({
      mutationKey: ["createOrderItemQuote"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        command: Parameters<
          typeof apiClient.orderItemQuotesControllerAddOrderItemsQuote
        >["0"]["orderItemQuoteAddCommand"];
      }) =>
        apiClient.orderItemQuotesControllerAddOrderItemsQuote({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemQuoteAddCommand: params.command,
        }),
      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  const useSendOrderItemQuotes = () =>
    useMutation({
      mutationKey: ["sendOrderItemQuote"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        command: Parameters<
          typeof apiClient.orderItemQuotesControllerSendOrderItemsQuotes
        >["0"]["orderItemQuotesSendCommand"];
      }) =>
        apiClient.orderItemQuotesControllerSendOrderItemsQuotes({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemQuotesSendCommand: params.command,
        }),
      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  const useDeleteOrderItemQuote = () =>
    useMutation({
      mutationKey: ["deleteOrderItemQuote"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        sandboxItemQuoteId: string;
      }) =>
        apiClient.orderItemQuotesControllerDeleteOrderItemsQuote({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          sandboxItemQuoteId: params.sandboxItemQuoteId,
        }),
      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  const useValidateOrderItemsQuote = () =>
    useMutation<
      Awaited<
        ReturnType<
          typeof apiClient.orderItemQuotesControllerValidateOrderItemsQuote
        >
      >,
      Error,
      { sandboxItemQuoteId: string; orderId: string; orderItemId: string }
    >({
      mutationKey: ["acceptSandboxItemQuotes"],
      mutationFn: ({ sandboxItemQuoteId, orderId }) =>
        apiClient.orderItemQuotesControllerValidateOrderItemsQuote({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: orderId,
          sandboxItemQuoteId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]),
    });

  const useRefuseOrderItemsQuotes = () =>
    useMutation({
      mutationKey: ["refuseSandboxItemQuotes"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        command: Parameters<
          typeof apiClient.orderItemQuotesControllerRefuseOrderItemsQuotes
        >["0"]["orderItemQuotesRefuseCommand"];
      }) =>
        apiClient.orderItemQuotesControllerRefuseOrderItemsQuotes({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemQuotesRefuseCommand: params.command,
        }),
      onSuccess: (_, params) => {
        void Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  const useCancelAllCurrentOrderItemQuotes = () =>
    useMutation({
      mutationKey: ["cancelAllCurrentOrderItemQuotes"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        cancelReason: string;
      }) =>
        apiClient.orderItemQuotesControllerCancelAllCurrentOrderItemQuotes({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemQuoteCancelCommand: {
            cancelReason: params.cancelReason,
          },
        }),
      onSuccess: (_, params) => {
        void Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  return {
    useAddOrderItemsQuote,
    useDeleteOrderItemQuote,
    useSendOrderItemQuotes,
    useValidateOrderItemsQuote,
    useRefuseOrderItemsQuotes,
    useCancelAllCurrentOrderItemQuotes,
  };
};
